import { useState } from "react";
import { createPortal } from "react-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { MessageSquareHeart, X, Star, Loader2, Send } from "lucide-react";

export const FeedbackWidget = () => {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const reset = () => {
    setRating(0);
    setHover(0);
    setMessage("");
  };

  const submit = async () => {
    if (rating < 1) {
      toast.error("Choisissez une note (1 à 5 étoiles)");
      return;
    }
    setSending(true);
    try {
      await api.post("/feedback", { rating, message: message.trim(), page: window.location.pathname });
      toast.success("Merci pour votre retour ! 🙏");
      setOpen(false);
      reset();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Échec de l'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        data-testid="feedback-open-btn"
        onClick={() => setOpen(true)}
        style={{ bottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
        className="fixed left-3 z-[55] flex items-center gap-2 h-11 pl-3 pr-4 rounded-full bg-primary text-primary-foreground shadow-lg shadow-black/15 text-sm font-medium hover:opacity-90 active:scale-95 transition"
      >
        <MessageSquareHeart className="w-4 h-4" /> Donner mon avis
      </button>

      {open &&
        createPortal(
          <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-3 sm:p-6" data-testid="feedback-modal">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !sending && setOpen(false)} />
            <div className="relative w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl animate-in-up">
              <button
                onClick={() => !sending && setOpen(false)}
                className="absolute top-4 right-4 w-8 h-8 grid place-items-center rounded-lg text-muted-foreground hover:bg-muted transition"
                aria-label="Fermer"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary grid place-items-center">
                <MessageSquareHeart className="w-5 h-5" />
              </div>
              <h2 className="font-heading text-xl font-bold tracking-tight mt-4">Votre avis compte</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Vous testez Iacover — dites-nous ce qui vous plaît ou ce qui pourrait être amélioré.
              </p>

              <div className="mt-5 flex items-center gap-1.5" data-testid="feedback-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    data-testid={`feedback-star-${n}`}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => setRating(n)}
                    className="p-1 transition-transform hover:scale-110"
                    aria-label={`${n} étoile${n > 1 ? "s" : ""}`}
                  >
                    <Star className={`w-7 h-7 transition-colors ${(hover || rating) >= n ? "fill-current text-ochre" : "fill-none text-muted-foreground/40"}`} />
                  </button>
                ))}
              </div>

              <textarea
                data-testid="feedback-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                maxLength={2000}
                placeholder="Un bug ? Une idée ? Ce que vous aimeriez voir…"
                className="mt-4 w-full p-3.5 rounded-xl border border-input bg-background text-sm outline-none focus:ring-2 focus:ring-ring/40 resize-none"
              />

              <button
                data-testid="feedback-submit-btn"
                onClick={submit}
                disabled={sending}
                className="mt-4 w-full h-11 rounded-xl bg-primary text-primary-foreground font-medium text-sm hover:opacity-90 disabled:opacity-60 transition flex items-center justify-center gap-2"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Envoyer mon avis
              </button>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default FeedbackWidget;
